import { useAppSelector } from 'app/hooks';
import { useGetQuoteDataByInstanceQuery } from 'features/api/project-api';
import { useCallback, useMemo } from 'react';
import { QuotedSystemSnapshotType } from 'utils/quoted-system-snapshot-types';
import { isSystemQuotedStatus } from 'utils/quoted-systems';

export function useQuotedSystemsSnapshots(): QuotedSystemSnapshotType[] {
  const { instanceUniqueId } = useAppSelector((state) => state.project);
  const { structureUniqueId } = useAppSelector((state) => state.structure);

  const { data: quotedSystemsData } = useGetQuoteDataByInstanceQuery(
    {
      instanceUniqueId: instanceUniqueId!,
      structureUniqueId: structureUniqueId!
    },
    { skip: !instanceUniqueId || !structureUniqueId }
  );

  return useMemo(
    () =>
      (quotedSystemsData ?? [])
        .map((row) => row.quoteData as QuotedSystemSnapshotType | undefined)
        .filter((s): s is QuotedSystemSnapshotType => Boolean(s)),
    [quotedSystemsData]
  );
}

export default function useQuotedUniqueIds(): Set<string> {
  const snapshots = useQuotedSystemsSnapshots();
  return useMemo(
    () =>
      new Set(
        snapshots
          .map((s) => s.uniqueId)
          .filter((id): id is string => Boolean(id))
      ),
    [snapshots]
  );
}

export function useQuotedSystemIds(): Set<string> {
  const snapshots = useQuotedSystemsSnapshots();
  return useMemo(
    () =>
      new Set(
        snapshots.map((s) => s.id).filter((id): id is string => Boolean(id))
      ),
    [snapshots]
  );
}

export function useIsSystemQuoted(): (system: {
  uniqueId: string;
  quoteStatus?: string;
}) => boolean {
  const quotedUniqueIds = useQuotedUniqueIds();
  const quotableSystems = useAppSelector(
    (state) => state.systems.quotableSystems
  );
  return useCallback(
    (system) => isSystemQuotedStatus(system, quotedUniqueIds, quotableSystems),
    [quotedUniqueIds, quotableSystems]
  );
}

export function useHasQuotedSystems(): boolean {
  const snapshots = useQuotedSystemsSnapshots();
  return snapshots.length > 0;
}

export function useHasQuotableSystems(): boolean {
  const selectedSystems = useAppSelector(
    (state) => state.systems.selectedSystems
  );
  const selectedDuctedSystems = useAppSelector(
    (state) => state.systems.selectedDuctedSystems
  );
  const isSystemQuoted = useIsSystemQuoted();
  return useMemo(
    () =>
      selectedSystems.some((s) => !isSystemQuoted(s)) ||
      selectedDuctedSystems.some((s) => s.quoteStatus !== 'sent'),
    [selectedSystems, selectedDuctedSystems, isSystemQuoted]
  );
}
